<script setup lang="ts">

import {inject, onMounted, ref} from "vue";

import {type GrupoPermissaoVm, type PermissaoSistemaVm} from "@/domain/type/grupoPermissao.type";
import {EModalId, type ModalGrupoPermissaoCadastro} from "@/domain/type/modal.type";
import GrupoPermissaoService from "@/domain/services/grupoPermissao.service";
import {useToastHelper} from "@/helpers/toast.helper";
import {useModalStore} from "@/stores/modal.store";
import {useEventsStore} from "@/stores/events.store";


const {showRequestError, showToastSuccess} = useToastHelper();

const modalStore = useModalStore();
const dialogRef: any = inject("dialogRef");

const {grupoPermissaoEvents} = useEventsStore();

const cgpe = ref("");
const codigoGrupoPermissao = ref(0);
const isModalVisualizacao = ref(false);

const isLoadingComponent = ref(true);
const isStateComponentError = ref(false);


const arrPermissoes = ref<PermissaoSistemaVm[]>([]);
const arrPermissoesProcessadas = ref<any>(null);
const permissoesSelecionadas = ref();
const grupoPermissao = ref<GrupoPermissaoVm>();

const descricaoGrupoPermissao = ref<string>();
const erroDescricaoGrupoPermissao = ref(false);

const isLoadingGravandoGrupoPermissao = ref(false);


//Metodo para processar o array de permissoes para a TreeView
const processarPermissoesParaTreeView = () => {

  //Ordena os elementos do array pela ordem
  arrPermissoes.value.sort((a, b) => a.ordem - b.ordem);

  // Criar um mapa para facilitar a busca por key
  const map: any = {};
  const tree: any = [];

  // Passo 1: Mapear todos os itens por sua key
  arrPermissoes.value.forEach(p => {
    map[p.codigoPermissao] = {
      key: p.codigoPermissao,
      label: '(' + p.codigoPermissao + ') ' + p.descricaoPermissao,
      codigoPai: p.codigoPermissao,
      ordem: p.ordem,
      icon: (p.codigoPermissao == 1 ? 'pi pi-sparkles' : ''),
      children: []
    }
  });

  // Passo 2: Aninhar os itens com base em parentKey
  arrPermissoes.value.forEach(perm => {
    if (perm.codigoPermissaoPai === 0) {
      // Se parentKey é 0, é um nó raiz
      tree.push(map[perm.codigoPermissao]);
    } else {
      // Adicionar o item ao array children do seu pai
      if (map[perm.codigoPermissaoPai]) {
        map[perm.codigoPermissaoPai].children.push(map[perm.codigoPermissao]);
      }
    }
  });


  arrPermissoesProcessadas.value = tree;

}

const processarPermissoesParaTreeViewVisualizacao = () => {

  //Obtem um array com as permissoes do usuario
  const arrPermissoesAtivas = Object.keys(permissoesSelecionadas.value);

  //Ordena os elementos do array pela ordem
  arrPermissoes.value.sort((a, b) => a.ordem - b.ordem);

  // Criar um mapa para facilitar a busca por key
  const map: any = {};
  const tree: any = [];

  // Passo 1: Mapear todos os itens por sua key
  arrPermissoes.value.forEach(p => {
    map[p.codigoPermissao] = {
      key: p.codigoPermissao,
      label: '(' + p.codigoPermissao + ') ' + p.descricaoPermissao,
      codigoPai: p.codigoPermissao,
      ordem: p.ordem,
      checked: (arrPermissoesAtivas.includes(p.codigoPermissao.toString())),
      children: []
    }
  });

  // Passo 2: Aninhar os itens com base em parentKey
  arrPermissoes.value.forEach(perm => {
    if (perm.codigoPermissaoPai === 0) {
      // Se parentKey é 0, é um nó raiz
      tree.push(map[perm.codigoPermissao]);
    } else {
      // Adicionar o item ao array children do seu pai
      if (map[perm.codigoPermissaoPai]) {
        map[perm.codigoPermissaoPai].children.push(map[perm.codigoPermissao]);
      }
    }
  });


  arrPermissoesProcessadas.value = tree;

}


const onGravarGrupoPermissao = () => {

  if (!descricaoGrupoPermissao.value || descricaoGrupoPermissao.value.trim().length <= 4) {
    erroDescricaoGrupoPermissao.value = true;
    return false;
  } else {
    erroDescricaoGrupoPermissao.value = false
  }

  let permissaoIds: number[] = [];

  if (permissoesSelecionadas.value) {
    let keys = Object.keys(permissoesSelecionadas.value);
    keys.forEach((key) => {
      if (permissoesSelecionadas.value[key].checked || permissoesSelecionadas.value[key].partialChecked) {
        permissaoIds.push(parseInt(key));
      }

    })
  }

  isLoadingGravandoGrupoPermissao.value = true;

  if (codigoGrupoPermissao.value === 0) {


    GrupoPermissaoService.cadastrar({
      cgpe: cgpe.value,
      descricao: descricaoGrupoPermissao.value,
      permissoesIds: permissaoIds
    })
        .then(() => {

          showToastSuccess("Grupo cadastrado com sucesso!", "");
          grupoPermissaoEvents.emitGrupoGravado(1);
          fecharModal();
        })
        .catch(erro => {
          showRequestError(erro)
        })
        .finally(() => {
          isLoadingGravandoGrupoPermissao.value = false;
        });


  } else {
    GrupoPermissaoService.alterar({
      codigoGrupoPermissao: codigoGrupoPermissao.value,
      cgpe: cgpe.value,
      descricao: descricaoGrupoPermissao.value,
      permissoesIds: permissaoIds
    })
        .then(() => {

          showToastSuccess("Grupo alterado com sucesso!", "");
          grupoPermissaoEvents.emitGrupoGravado(1);
          fecharModal();


        })
        .catch(erro => {
          showRequestError(erro)
        })
        .finally(() => {
          isLoadingGravandoGrupoPermissao.value = false;
        });
  }
}

const fecharModal = () => {
  if (dialogRef && dialogRef.value) {
    dialogRef.value.close();
  }
}

const alimentaCamposGrupoPermissao = (grupoPermissao: GrupoPermissaoVm) => {

  codigoGrupoPermissao.value = grupoPermissao.codigo;
  descricaoGrupoPermissao.value = grupoPermissao.descricao;

  permissoesSelecionadas.value = [];

  grupoPermissao.permissoes.forEach((permissao) => {
    permissoesSelecionadas.value[permissao.codigoPermissao] = {
      checked: true
    }
  })


  //Se a visualização estiver ativa, exibe os icones das permissoes


  if (isModalVisualizacao.value) {
    processarPermissoesParaTreeViewVisualizacao();
  }


}


const consultarGrupoPermissao = () => {

  isLoadingComponent.value = true;

  GrupoPermissaoService.listar({
    cgpe: cgpe.value,
    codigoGrupoPermissao: codigoGrupoPermissao.value
  })
      .then(ret => {

        let gp = ret.data.find(gp => gp.codigo == codigoGrupoPermissao.value);
        if (gp) {
          alimentaCamposGrupoPermissao(gp);
        }

      })
      .finally(() => {
        isLoadingComponent.value = false;
      })

}


const carregarMasterdata = () => new Promise((resolve, reject) => {

  try {

    GrupoPermissaoService.listarPermissoes()
        .then(ret => {

          arrPermissoes.value = ret.data;
          processarPermissoesParaTreeView();
          resolve(true);

        })
        .catch((error) => {

          showRequestError(error);

          reject("masterdataError-request")
          throw ("masterdataError-request");
        })


  } catch {
    reject("masterdataError");
  }

})

onMounted(() => {

  try {

    //Verifica se possui informacoes atraves das PROPS (componente adicionado em um template) ou MODAL
    const modalParams = modalStore.getModal(EModalId.modalGrupoPermissaoCadastro)?.getParam() as ModalGrupoPermissaoCadastro;

    if (modalParams) {
      //Carrega atraves de um modal

      if (modalParams.propCodigoGrupoPermissao) {
        codigoGrupoPermissao.value = modalParams.propCodigoGrupoPermissao;
      }

      if (modalParams.propCgpe) {
        cgpe.value = modalParams.propCgpe;
      }

      if (modalParams.propIsVisualizacao) {
        isModalVisualizacao.value = modalParams.propIsVisualizacao;
      }

      if (modalParams.propGrupoPermissao) {
        if (modalParams.propGrupoPermissao.codigo > 0) {
          grupoPermissao.value = modalParams.propGrupoPermissao;

        } else {
          throw ("error");
        }
      }


    }

    //define o status do componente com erro
    isStateComponentError.value = !(cgpe.value != "")

  } catch {
    isStateComponentError.value = true;
  }


  // Se não estiver com erro verifica se é alteração e ativa consulta do usuário
  if (!isStateComponentError.value) {

    carregarMasterdata()
        .then(() => {

          if (grupoPermissao.value != undefined) {
            alimentaCamposGrupoPermissao(grupoPermissao.value);
            isLoadingComponent.value = false;
          } else if (codigoGrupoPermissao.value > 0) {
            consultarGrupoPermissao();
          } else {
            isLoadingComponent.value = false;
          }


        })
        .catch(() => {
          isStateComponentError.value = true;
        })

  }

})

</script>

<template>

  <div v-if="!isStateComponentError">


    <div v-if="isLoadingComponent">

      <div class="mt-6">
        <Skeleton height="2.4rem"/>
      </div>

      <div class=" mt-5">
        <Skeleton height="14rem"/>
      </div>

      <div class="mt-5">
        <Skeleton height="2.4rem"/>
      </div>

    </div>


    <div v-if="!isLoadingComponent">

      <div class="mt-6">
        <FloatLabel>
          <InputText id="descricao" class="w-full" v-model="descricaoGrupoPermissao"
                     :invalid="erroDescricaoGrupoPermissao" :readonly="isModalVisualizacao"/>
          <label for="descricao">Descrição</label>
        </FloatLabel>
        <small class="p-error" v-if="erroDescricaoGrupoPermissao">
          A descrição deve conter no mínimo 4 caracteres
        </small>
      </div>


      <div class="mt-5 mb-1">
        <label>Permissões</label>
      </div>

      <div class=" border h-[25rem] overflow-y-scroll">
        <Tree :value="arrPermissoesProcessadas" v-model:selection-keys="permissoesSelecionadas"
              :selectionMode="isModalVisualizacao ? '' : 'checkbox'"

        >
          <template v-if="isModalVisualizacao" #nodeicon="slotProps">

            <i v-if="slotProps.node.key == '1' && slotProps.node.checked "
               class="pi pi-sparkles text-[--p-button-warn-background]"> </i>

            <i v-if="slotProps.node.key != '1' && !slotProps.node.checked "
               class="pi pi-ban text-[--p-button-danger-background]"> </i>

            <i v-if="slotProps.node.key != '1' && slotProps.node.checked "
               class="pi pi-check-circle text-[--p-button-success-background]"> </i>


          </template>
        </Tree>
      </div>


      <div class="mt-5 text-center mb-3" v-if="!isModalVisualizacao">
        <Button label="Gravar" icon="pi pi-save" @click="onGravarGrupoPermissao" :loading="isLoadingGravandoGrupoPermissao"></Button>
      </div>

    </div>

  </div>

  <div v-if="isStateComponentError" class="component-state-error">
    <span class="p-error text-2xl">Component Error</span>
  </div>

</template>

<style scoped>

</style>